import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { BarChartPro } from "@mui/x-charts-pro/BarChartPro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FuelCellRow } from "../models/FuelCellRow";

interface Props {
  rows: FuelCellRow[];
}

export function FuelCellUsageChart({ rows }: Props) {
  const { t } = useTranslation();

  const data = useMemo(() => {
    return rows
      .filter(r => (r.usedRangeKg ?? 0) > 0 || (r.refillCount ?? 0) > 0)
      .map(r => ({
        label: r.licensePlate || r.name || r.id,
        used: Number((r.usedRangeKg ?? 0).toFixed(2)),
        refills: r.refillCount ?? 0,
      }))
      .sort((a, b) => b.used - a.used)
      .slice(0, 30);
  }, [rows]);

  if (data.length === 0) {
    return (
      <Card sx={{ mx: 2, flexShrink: 0 }}>
        <CardContent>
          <Typography variant="subtitle1">
            {t("fuelCell.chart.title", "Usage & refills per vehicle")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("fuelCell.chart.noData", "No usage recorded in selected range")}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mx: 2, flexShrink: 0 }}>
      <CardContent sx={{ pb: 1 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t("fuelCell.chart.title", "Usage & refills per vehicle")}
        </Typography>
        <BarChartPro
          dataset={data}
          leftAxis="usedAxis"
          rightAxis="refillAxis"
          xAxis={[
            {
              scaleType: "band",
              dataKey: "label",
              tickLabelStyle: {
                angle: -35,
                textAnchor: "end",
                fontSize: 11,
              },
            },
          ]}
          yAxis={[
            {
              id: "usedAxis",
              position: "left",
              valueFormatter: (value: number) => `${value} kg`,
            },
            {
              id: "refillAxis",
              position: "right",
              tickMinStep: 1,
            },
          ]}
          series={[
            {
              dataKey: "used",
              label: t("fuelCell.chart.usedKg", "Used (kg)"),
              yAxisId: "usedAxis",
              valueFormatter: v => (v == null ? "" : `${v.toFixed(2)} kg`),
            },
            {
              dataKey: "refills",
              label: t("fuelCell.chart.refills", "Refills"),
              yAxisId: "refillAxis",
              valueFormatter: v => (v == null ? "" : String(v)),
            },
          ]}
          height={380}
          margin={{ top: 40, right: 70, bottom: 110, left: 70 }}
          slotProps={{
            legend: {
              direction: "row",
              position: { vertical: "top", horizontal: "middle" },
            },
          }}
        />
      </CardContent>
    </Card>
  );
}
